'use client';
import React from 'react';
import Link from 'next/link';
import { ArrowRightIcon, LinkIcon, ArrowPathIcon, ShoppingBagIcon, BoltIcon, CheckCircleIcon,  } from '@heroicons/react/24/outline';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Icon from '@/components/ui/AppIcon';


const fbFeatures = [
  { title: 'Facebook Shop Sync', desc: 'Automatically sync your ERP product catalog to your Facebook Shop. Prices, stock levels, and descriptions update in real time.' },
  { title: 'Order Management', desc: 'Facebook orders flow directly into MANGQASHA IT SOLUTIONS. Fulfill, track, and manage returns from a single dashboard.' },
  { title: 'Ad Audience Targeting', desc: 'Use your ERP customer data to build lookalike audiences and retargeting lists in Facebook Ads Manager.' },
];

const igFeatures = [
  { title: 'Shoppable Posts & Reels', desc: 'Tag products directly in Instagram posts and Reels. Customers tap to buy without leaving the app.' },
  { title: 'Story Product Links', desc: 'Add product stickers to Instagram Stories. Drive traffic to your ERP-powered storefront with one tap.' },
  { title: 'DM Order Automation', desc: 'Respond to Instagram DMs with automated product links and order confirmations powered by MANGQASHA IT SOLUTIONS.' },
];

const metrics = [
  { label: 'Avg Social Revenue / Month', val: '$48,200', change: '+23%' },
  { label: 'Facebook Reach', val: '142K', change: '+18%' },
  { label: 'Instagram Conversions', val: '3.8%', change: '+0.9%' },
  { label: 'Social Orders This Month', val: '847', change: '+31%' },
];

const steps = [
  {
    icon: LinkIcon,
    step: '01',
    title: 'Connect Your Channels',
    desc: 'Link your Facebook Business account and Instagram profile to MANGQASHA IT SOLUTIONS in under 5 minutes. OAuth-secured, no developer needed.',
  },
  {
    icon: ArrowPathIcon,
    step: '02',
    title: 'Sync Your Product Catalog',
    desc: 'MANGQASHA IT SOLUTIONS automatically pushes your product listings, pricing, and inventory to both platforms. Changes sync in real time.',
  },
  {
    icon: ShoppingBagIcon,
    step: '03',
    title: 'Start Selling',
    desc: 'Social orders appear in your MANGQASHA IT SOLUTIONS order queue alongside all other channels. One fulfillment workflow for everything.',
  },
];

export default function SocialSellingPage() {
  return (
    <div className="min-h-screen bg-[#0F1B2D]">
      <Navbar />
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="section-label mb-3">Social Commerce</p>
            <h1 className="text-4xl font-extrabold text-white mb-5 leading-tight">
              Turn Your Social Followers<br />Into ERP Customers
            </h1>
            <p className="text-[#94A3B8] text-base leading-relaxed mb-8">
              Connect MANGQASHA IT SOLUTIONS to Facebook and Instagram. Sync your product catalog, manage social orders, and track social revenue — all from the same platform you use to run your business.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link href="/book-demo" className="btn-primary px-6 py-3 text-sm inline-flex items-center gap-2">
                Connect Your Channels <ArrowRightIcon className="w-4 h-4" />
              </Link>
              <Link href="/products" className="btn-outline px-6 py-3 text-sm">
                View Plans
              </Link>
            </div>
          </div>

          {/* Social metrics mockup */}
          <div className="bg-[#162236] border border-[#1E3352] rounded-2xl p-6 teal-glow">
            <div className="flex items-center justify-between mb-5">
              <p className="text-sm font-semibold text-white">Social Commerce Overview</p>
              <span className="text-xs text-[#00C9A7] bg-[#00C9A7]/10 px-2 py-1 rounded-full">Live</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {metrics?.map((m) => (
                <div key={m?.label} className="bg-[#0F1B2D] border border-[#1E3352] rounded-xl p-3">
                  <p className="text-[10px] text-[#64748B] mb-1">{m?.label}</p>
                  <p className="text-lg font-bold text-white">{m?.val}</p>
                  <p className="text-xs font-semibold text-[#00C9A7]">{m?.change} this month</p>
                </div>
              ))}
            </div>
            <div className="mt-4 flex items-center gap-3">
              <div className="flex-1 bg-[#0F1B2D] border border-[#1E3352] rounded-xl p-3 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#1877F2] flex items-center justify-center shrink-0">
                  <span className="text-white font-black text-sm">f</span>
                </div>
                <div>
                  <p className="text-xs font-semibold text-white">Facebook Shop</p>
                  <p className="text-[10px] text-[#00C9A7]">● Connected</p>
                </div>
              </div>
              <div className="flex-1 bg-[#0F1B2D] border border-[#1E3352] rounded-xl p-3 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] flex items-center justify-center shrink-0">
                  <span className="text-white font-black text-xs">IG</span>
                </div>
                <div>
                  <p className="text-xs font-semibold text-white">Instagram</p>
                  <p className="text-[10px] text-[#00C9A7]">● Connected</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* How it works — horizontal cards */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="mb-10 text-center">
          <p className="section-label mb-2">How It Works</p>
          <h2 className="text-3xl font-extrabold text-white">Three steps to social commerce</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {steps?.map((s, i) => {
            const Icon = s?.icon;
            return (
              <div key={i} className="bg-[#162236] border border-[#1E3352] rounded-2xl p-6 relative overflow-hidden card-hover">
                <div className="absolute top-4 right-4 text-5xl font-black text-[#1E3352] select-none">{s?.step}</div>
                <div className="w-10 h-10 rounded-xl bg-[#00C9A7]/10 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-[#00C9A7]" />
                </div>
                <h3 className="text-base font-bold text-white mb-2">{s?.title}</h3>
                <p className="text-sm text-[#64748B] leading-relaxed">{s?.desc}</p>
              </div>
            );
          })}
        </div>
      </section>
      {/* Facebook Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-[#1877F2] flex items-center justify-center">
                <span className="text-white font-black">f</span>
              </div>
              <div>
                <p className="section-label">Facebook Integration</p>
                <h2 className="text-2xl font-extrabold text-white">Sell more with Facebook Shop</h2>
              </div>
            </div>
            <div className="space-y-4">
              {fbFeatures?.map((f) => (
                <div key={f?.title} className="flex gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-[#00C9A7] shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-white mb-0.5">{f?.title}</p>
                    <p className="text-sm text-[#64748B]">{f?.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] flex items-center justify-center">
                <span className="text-white font-black text-sm">IG</span>
              </div>
              <div>
                <p className="section-label">Instagram Integration</p>
                <h2 className="text-2xl font-extrabold text-white">Convert followers to buyers</h2>
              </div>
            </div>
            <div className="space-y-4">
              {igFeatures?.map((f) => (
                <div key={f?.title} className="flex gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-[#00C9A7] shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-white mb-0.5">{f?.title}</p>
                    <p className="text-sm text-[#64748B]">{f?.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-[#162236] border border-[#1E3352] rounded-3xl p-10 text-center">
          <BoltIcon className="w-10 h-10 text-[#00C9A7] mx-auto mb-4" />
          <h2 className="text-2xl font-extrabold text-white mb-3">Ready to sell on social?</h2>
          <p className="text-[#94A3B8] text-sm mb-6 max-w-md mx-auto">
            Social selling is included in Professional and Enterprise plans. Book a demo to see it live.
          </p>
          <Link href="/book-demo" className="btn-primary px-8 py-3 text-sm inline-flex items-center gap-2">
            Book a Demo <ArrowRightIcon className="w-4 h-4" />
          </Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
